/**
 * Accent (brand) color — 6 предустановленных вариантов. Сохраняется
 * в localStorage. Применяется поверх базовой темы:
 *  - в AntD через `brandOverride` в `buildAntdTheme`
 *  - в CSS-переменных через `accentVarsFor` (ThemeModeProvider мерджит
 *    их поверх базовых vars)
 */

import {atomWithStorage} from 'jotai/utils';

import type {ThemeMode} from '@styles/tokens';

export type AccentChoice = 'blue' | 'violet' | 'mint' | 'coral' | 'amber' | 'slate';

export type AccentOption = {
  value: AccentChoice;
  label: string;
  preview: string;
};

export const ACCENT_CHOICES: readonly AccentOption[] = [
  {value: 'blue', label: 'Синий', preview: '#3B82F6'},
  {value: 'violet', label: 'Фиолетовый', preview: '#7C3AED'},
  {value: 'mint', label: 'Мятный', preview: '#10B981'},
  {value: 'coral', label: 'Коралловый', preview: '#F43F5E'},
  {value: 'amber', label: 'Янтарный', preview: '#F59E0B'},
  {value: 'slate', label: 'Графит', preview: '#475569'},
];

type AccentPalette = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  rgb: string;
};

const PALETTES: Record<AccentChoice, AccentPalette> = {
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
    rgb: '59, 130, 246',
  },
  violet: {
    50: '#F5F3FF',
    100: '#EDE9FE',
    200: '#DDD6FE',
    300: '#C4B5FD',
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',
    700: '#6D28D9',
    800: '#5B21B6',
    900: '#4C1D95',
    rgb: '139, 92, 246',
  },
  mint: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#10B981',
    600: '#059669',
    700: '#047857',
    800: '#065F46',
    900: '#064E3B',
    rgb: '16, 185, 129',
  },
  coral: {
    50: '#FFF1F2',
    100: '#FFE4E6',
    200: '#FECDD3',
    300: '#FDA4AF',
    400: '#FB7185',
    500: '#F43F5E',
    600: '#E11D48',
    700: '#BE123C',
    800: '#9F1239',
    900: '#881337',
    rgb: '244, 63, 94',
  },
  amber: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
    rgb: '245, 158, 11',
  },
  slate: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
    rgb: '71, 85, 105',
  },
};

const ACCENT_STORAGE_KEY = 'kassir:accent';

export const accentChoiceAtom = atomWithStorage<AccentChoice>(
  ACCENT_STORAGE_KEY,
  'blue',
  undefined,
  {getOnInit: true},
);

// AntD brandOverride: цвета, которые красят все brand-зависимые
// AntD-компоненты (Button primary, Switch, Tabs, Menu selected).
export const antdBrandOverrideFor = (accent: AccentChoice, mode: ThemeMode) => {
  const p = PALETTES[accent];
  if (mode === 'light') {
    return {
      brand: p[500],
      brandHover: p[600],
      brandActive: p[700],
      brandSoft: p[50],
      textOnBrand: '#FFFFFF',
      shadowBrand: `0 6px 18px rgba(${p.rgb}, 0.35)`,
    };
  }
  return {
    brand: p[400],
    brandHover: p[300],
    brandActive: p[500],
    brandSoft: `rgba(${p.rgb}, 0.14)`,
    textOnBrand: '#FFFFFF',
    shadowBrand: `0 6px 18px rgba(${p.rgb}, 0.55)`,
  };
};

// CSS-переменные акцента — мерджатся поверх базовых tokens.
export const accentVarsFor = (accent: AccentChoice, mode: ThemeMode): Record<string, string> => {
  const p = PALETTES[accent];
  if (mode === 'light') {
    return {
      '--color-brand': p[500],
      '--color-brand-hover': p[600],
      '--color-brand-active': p[700],
      '--color-brand-soft': p[50],
      '--color-brand-border': p[200],
      '--color-brand-text': p[700],
      '--color-info': p[500],
      '--color-info-soft': p[50],
      '--color-info-text': p[700],
      '--color-sider-item-active': p[500],
      '--shadow-focus': `0 0 0 3px rgba(${p.rgb}, 0.25)`,
      '--shadow-brand': `0 6px 18px rgba(${p.rgb}, 0.35)`,
      '--accent-rgb': p.rgb,
    };
  }
  return {
    '--color-brand': p[400],
    '--color-brand-hover': p[300],
    '--color-brand-active': p[500],
    '--color-brand-soft': `rgba(${p.rgb}, 0.14)`,
    '--color-brand-border': p[700],
    '--color-brand-text': p[300],
    '--color-info': p[400],
    '--color-info-soft': `rgba(${p.rgb}, 0.14)`,
    '--color-info-text': p[300],
    '--color-sider-item-active': p[400],
    '--shadow-focus': `0 0 0 3px rgba(${p.rgb}, 0.35)`,
    '--shadow-brand': `0 6px 18px rgba(${p.rgb}, 0.55)`,
    '--accent-rgb': p.rgb,
  };
};
